import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useBrand } from "@/contexts/BrandContext";
import { toast } from "sonner";
import { Settings2, RefreshCw, Server, Wifi, Key, Save, CloudCog } from "lucide-react";
import { GlassCard, PillButton, SectionTitle } from "./_shared";
import { Switch } from "@/components/ui/switch";

export default function AdminSystem() {
  const { refreshBrand } = useBrand();
  const [offline, setOffline] = useState(null);
  const [config, setConfig] = useState(null);
  const [branding, setBranding] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [o, c, s] = await Promise.all([
        api.get("/admin/offline/stats"),
        api.get("/admin/config"),
        api.get("/admin/settings"),
      ]);
      setOffline(o.data);
      setConfig(c.data);
      setBranding(s.data);
    } catch {
      /* noop */
    }
  };
  useEffect(() => {
    load();
  }, []);

  const bustCache = async () => {
    try {
      const { data } = await api.post("/admin/cache/bust");
      toast.success(`Cache bumped → ${data.cache_version}`);
      load();
    } catch { toast.error("Erreur"); }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        brand_name: branding.brand_name,
        tagline: branding.tagline,
        logo_url: branding.logo_url,
        primary_color: branding.primary_color,
        accent_color: branding.accent_color,
        support_email: branding.support_email,
        offline_enabled: branding.offline_enabled,
        auto_updates: branding.auto_updates,
      });
      await refreshBrand();
      toast.success("Branding enregistré");
    } catch { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  if (!offline || !config || !branding) return <div className="text-zinc-400 text-sm">Chargement…</div>;

  return (
    <div className="space-y-5">
      {/* PWA / Offline */}
      <div>
        <SectionTitle icon={Wifi}>PWA & hors-ligne</SectionTitle>
        <GlassCard className="p-4 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">Cache</div>
              <div className="font-display font-bold text-xl text-[var(--text)] tabular-nums">{offline.cache_version}</div>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">Offline</div>
              <div className={`font-display font-bold text-xl tabular-nums ${offline.offline_enabled ? "text-emerald-300" : "text-zinc-400"}`}>
                {offline.offline_enabled ? "ON" : "OFF"}
              </div>
            </div>
            <div className="text-center">
              <div className="text-[10px] uppercase tracking-wider text-zinc-400">Auto-MAJ</div>
              <div className={`font-display font-bold text-xl tabular-nums ${offline.auto_updates ? "text-emerald-300" : "text-zinc-400"}`}>
                {offline.auto_updates ? "ON" : "OFF"}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-black/5">
            <div>
              <div className="text-sm font-semibold text-[var(--text)]">Mode hors-ligne</div>
              <div className="text-[11px] text-zinc-400">Queue locale + cache prospects</div>
            </div>
            <Switch
              checked={!!branding.offline_enabled}
              onCheckedChange={(v) => setBranding({ ...branding, offline_enabled: v })}
              data-testid="sys-toggle-offline"
            />
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-black/5">
            <div>
              <div className="text-sm font-semibold text-[var(--text)]">Mises à jour auto</div>
              <div className="text-[11px] text-zinc-400">Service worker silencieux</div>
            </div>
            <Switch
              checked={!!branding.auto_updates}
              onCheckedChange={(v) => setBranding({ ...branding, auto_updates: v })}
              data-testid="sys-toggle-autoupdate"
            />
          </div>
          <PillButton onClick={bustCache} testId="sys-bust-cache" className="w-full h-10">
            <RefreshCw className="w-3.5 h-3.5 inline mr-1" /> Forcer le rafraîchissement des clients
          </PillButton>
        </GlassCard>
      </div>

      {/* Branding */}
      <div>
        <SectionTitle icon={CloudCog}>Branding</SectionTitle>
        <GlassCard className="p-4 space-y-3">
          <div>
            <div className="text-xs font-semibold text-zinc-400 mb-1.5">Nom de la marque</div>
            <input
              value={branding.brand_name || ""}
              onChange={(e) => setBranding({ ...branding, brand_name: e.target.value })}
              className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-xl px-3 h-10 text-sm text-[var(--text)] outline-none focus:border-black"
              data-testid="sys-brand-name"
            />
          </div>
          <div>
            <div className="text-xs font-semibold text-zinc-400 mb-1.5">Tagline</div>
            <input
              value={branding.tagline || ""}
              onChange={(e) => setBranding({ ...branding, tagline: e.target.value })}
              className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-xl px-3 h-10 text-sm text-[var(--text)] outline-none focus:border-black"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-xs font-semibold text-zinc-400 mb-1.5">Couleur primaire</div>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={branding.primary_color || "#00DC82"}
                  onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                  className="w-10 h-10 rounded-xl border border-[var(--border)] bg-transparent cursor-pointer"
                />
                <input
                  value={branding.primary_color || ""}
                  onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                  className="flex-1 bg-[var(--surface)] border border-[var(--border)] rounded-xl px-3 h-10 text-sm text-[var(--text)] outline-none font-mono"
                />
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-zinc-400 mb-1.5">Accent</div>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={branding.accent_color || "#D946EF"}
                  onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                  className="w-10 h-10 rounded-xl border border-[var(--border)] bg-transparent cursor-pointer"
                />
                <input
                  value={branding.accent_color || ""}
                  onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                  className="flex-1 bg-[var(--surface)] border border-[var(--border)] rounded-xl px-3 h-10 text-sm text-[var(--text)] outline-none font-mono"
                />
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-zinc-400 mb-1.5">Email support</div>
            <input
              value={branding.support_email || ""}
              onChange={(e) => setBranding({ ...branding, support_email: e.target.value })}
              className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-xl px-3 h-10 text-sm text-[var(--text)] outline-none focus:border-black"
            />
          </div>
          <PillButton onClick={saveBranding} disabled={saving} variant="primary" testId="sys-save-branding" className="w-full h-11">
            <Save className="w-3.5 h-3.5 inline mr-1" />{saving ? "Enregistrement…" : "Enregistrer"}
          </PillButton>
        </GlassCard>
      </div>

      {/* Integrations / Config */}
      <div>
        <SectionTitle icon={Key}>Intégrations & clés API</SectionTitle>
        <GlassCard className="p-4 space-y-2">
          {Object.entries(config.integrations).map(([k, v]) => {
            const isPappers = k === "pappers_api_key";
            const label = {
              emergent_llm_key: "Emergent LLM (Claude/GPT/Gemini)",
              stripe_api_key: "Stripe (paiements)",
              resend_api_key: "Resend (emails)",
              pappers_api_key: "Pappers.fr (enrichissement premium)",
              inpi_api_url: "SIRENE / INPI (fallback)",
              nominatim_url: "Nominatim (géocodage)",
            }[k] || k;
            const isConfigured = typeof v === "string" && v.length > 0 && !v.startsWith("https://");
            const isPublic = typeof v === "string" && v.startsWith("https://");
            return (
              <div key={k} className="flex items-center justify-between gap-3 py-2 border-b border-black/5 last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-[var(--text)]">{label}</div>
                  <div className={`text-[11px] font-mono truncate ${isConfigured ? "text-emerald-300" : isPublic ? "text-zinc-400" : "text-rose-300"}`}>
                    {v || "non configuré"}
                  </div>
                </div>
                {isPappers && !isConfigured && (
                  <a
                    href="https://www.pappers.fr/api"
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 text-[11px] font-semibold text-[var(--text)] underline underline-offset-2 hover:text-emerald-400"
                  >
                    Activer →
                  </a>
                )}
              </div>
            );
          })}
        </GlassCard>
      </div>

      <div>
        <SectionTitle icon={Server}>Base de données</SectionTitle>
        <GlassCard className="p-4">
          <div className="flex items-center justify-between py-1.5">
            <div className="text-xs text-zinc-300">MongoDB</div>
            <div className={`text-[11px] font-mono ${config.database.mongo_connected ? "text-emerald-300" : "text-rose-300"}`}>
              {config.database.mongo_connected ? "connectée" : "déconnectée"}
            </div>
          </div>
          <div className="flex items-center justify-between py-1.5 border-t border-black/5">
            <div className="text-xs text-zinc-300">db_name</div>
            <div className="text-[11px] font-mono text-zinc-400">{config.database.db_name}</div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
